/**
 * Facet dropdown options for the Customers list, extracted from the page so
 * the option lists can be unit tested without mounting the table.
 *
 * The keys here are HALF of a contract with `Api::V1::CustomersController`'s
 * `filterable(...)` declaration; the other half is pinned by
 * `components/table/config-parity.test.ts` via `CUSTOMERS_TABLE.facetKeys`.
 */
import type { FacetDef } from "@crm-src/components/table/TableFilterBar";
import type { FacetKeysOf } from "@crm-src/components/table/configs";
import { CUSTOMERS_TABLE } from "@crm-src/components/table/configs";
import { personLabel, type PersonLike } from "@crm-src/components/table/person-label";
import type { CustomerFilters } from "@crm-src/types/customer";

/**
 * The Customers facet keys, DERIVED from the shared config rather than
 * re-typed — so there is exactly one place a key can be misspelled, and
 * misspelling it there breaks the assertion below.
 */
export type CustomerFacetKey = FacetKeysOf<typeof CUSTOMERS_TABLE>;

/**
 * Compile-time proof that every declared facet key is a param `CustomerFilters`
 * actually carries — i.e. one the API implements. Before this, a key that only
 * existed in the config compiled fine and failed at RUNTIME as a param the
 * server dropped, with `config-parity.test.ts` as the only net. Now it is a
 * type error at the config, which is where the mistake is made.
 */
type FacetKeysAreFilterFields =
  CustomerFacetKey extends keyof CustomerFilters ? true : never;
const _facetKeysAreFilterFields: FacetKeysAreFilterFields = true;
void _facetKeysAreFilterFields;

interface ResellerLike { id: number | string; name: string }

/**
 * Business categories, mirroring `customers.business_category`.
 *
 * These are the ONLY two values `Customer` validates
 * (`xop-brain/apps/crm-api/app/models/customer.rb`: `inclusion: { in: %w[service
 * equipment_purchaser] }`), and the request param is named `business_type`
 * because that is the column's display name in the table — the controller maps
 * `business_type:` → `:business_category`. A value outside this pair is not a
 * loud error anywhere: the dropdown renders, the query goes out, and the server
 * matches zero rows. Pinned by `customers-page-query.test.ts`.
 */
const BUSINESS_TYPES: FacetDef["options"] = [
  { value: "service", label: "Service" },
  { value: "equipment_purchaser", label: "Equipment" },
];

/**
 * Support tiers, mirroring `customers.support_level` — validated `in: %w[none
 * weekdays 24_7]`. Same silent-zero-rows hazard as above; same pin.
 */
const SUPPORT_LEVELS: FacetDef["options"] = [
  { value: "none", label: "None" },
  { value: "weekdays", label: "Weekdays" },
  { value: "24_7", label: "24/7" },
];

/**
 * Build the Customers filter-bar facets.
 *
 * Options come from data the page ALREADY loads — the reseller list, the user
 * roster, the shared industry catalogue — so no endpoint exists just to
 * populate a dropdown. "Direct" is the reseller-less case and maps to the
 * server's `reseller_id=none` sentinel (the controller turns it into NULL, and
 * it mixes with real ids); it leads the list because most customers are direct.
 *
 * Returns every declared facet, INCLUDING one whose options came back empty
 * (`useUsers()` answers 403 with `[]` for a non-admin): dropping an optionless
 * facet from the rendered controls is `TableFilterBar`'s job, so every list
 * page inherits it. Hiding a facet on PERMISSION — the Customers page drops
 * `reseller_id` for a viewer without `crm:resellers` — is the caller's job.
 * Either way this stays a pure function of its inputs.
 */
export function buildCustomerFacetDefs(
  resellers: ResellerLike[],
  users: PersonLike[],
  industries: readonly string[],
): FacetDef<CustomerFacetKey>[] {
  return [
    {
      key: "reseller_id",
      label: "Reseller",
      options: [
        { value: "none", label: "Direct" },
        ...resellers.map((r) => ({ value: String(r.id), label: r.name })),
      ],
    },
    { key: "business_type", label: "Business Type", options: BUSINESS_TYPES },
    { key: "support_level", label: "Support Level", options: SUPPORT_LEVELS },
    {
      key: "salesperson_id",
      label: "Salesperson",
      options: users.map((u) => ({
        value: String(u.id),
        label: personLabel(u),
      })),
    },
    {
      key: "industry",
      label: "Industry",
      options: industries.map((i) => ({ value: i, label: i })),
    },
  ];
}

interface DealLike { renewal_date?: string | null }

/**
 * The value the Customers table's "Next Renewal" cell shows: the EARLIEST
 * still-upcoming `renewal_date` across the customer's deals, or null.
 *
 * Extracted out of `CustomersPage` so the boundary below is testable without
 * mounting the table.
 *
 * IT MUST AGREE WITH THE SERVER'S SORT for that column, which is
 *
 *   (SELECT MIN(cd.renewal_date) FROM customer_deals cd
 *     WHERE cd.customer_id = customers.id AND cd.renewal_date >= CURRENT_DATE)
 *
 * — note `>=`. This used to compute `new Date(deal.renewal_date) > now`, a
 * wall-clock comparison against a DATE-only column, so a renewal dated TODAY
 * counted server-side and not client-side: the header ordered rows by a value
 * one of them was not showing, for one day per deal. Worse, it was
 * timezone-dependent — `new Date("2026-08-28")` parses as UTC midnight, so east
 * of UTC the row read as past from the moment the local day began, and west of
 * it the cell would flip mid-afternoon.
 *
 * The CLIENT moved rather than the server: `renewal_date` is a DATE column with
 * no time of day, so no time of day belongs in the comparison, and a renewal
 * due today has not lapsed — dropping it from the cell is the answer an
 * operator would call wrong. Comparing ISO date STRINGS (`YYYY-MM-DD`, which
 * sort lexicographically) keeps the whole thing off the Date-parsing timezone
 * question entirely.
 */
export function nextRenewalDate(customer: { customer_deals?: DealLike[] | null }): string | null {
  const deals = customer.customer_deals;
  if (!deals || deals.length === 0) return null;

  const today = todayIsoDate();
  const upcoming = deals
    .map((deal) => deal.renewal_date)
    .filter((date): date is string => Boolean(date) && date!.slice(0, 10) >= today)
    .sort();

  return upcoming[0] ?? null;
}

/**
 * Today in the viewer's own timezone as `YYYY-MM-DD`.
 *
 * Deliberately NOT `new Date().toISOString().slice(0, 10)`, which is today in
 * UTC — that is the same timezone bug in a different costume.
 */
function todayIsoDate(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
